/*
 * Mobile sticky add-to-cart bar + "Choose your fit" bottom sheet.
 * Markup: snippets/sticky-atc.liquid — styles: assets/sticky-atc.css
 *
 * THEME UPDATE NOTE:
 * Must load AFTER theme.js (needs theme.Currency and theme.a11y):
 *   <script src="{{ 'sticky-atc.js' | asset_url }}" defer="defer"></script>
 *
 * The theme's own variant inputs are the single source of truth. Sheet taps drive the
 * real radios/selects (via .click(), so variant-image-filter.js's capture-phase hook
 * still runs first) and every repaint reconciles state back from those inputs.
 * Adding CLICKS the real inline add-to-cart button. It does NOT submit the form: a
 * form.requestSubmit() fires no click, slips past the Monster Upsells app's click hijack,
 * and every sticky add put TWO units in the cart. Read submitAdd() before changing this.
 */

(function () {
  'use strict';

  var SHEET_ANIM_MS = 340;
  var FLASH_MS = 1100;
  var ADD_TIMEOUT_MS = 6000;
  var LOADING_CLEAR_MS = 1500;
  var BAR_ERROR_MS = 4000;
  // Swipe-to-dismiss. 90px is far enough past the ~10px of slop a browser still calls a tap
  // that it can only be deliberate, while staying a small fraction of the sheet — about a
  // seventh of its height at the 82vh cap (~13% on an 844px viewport, ~16% at 667px), so an
  // exploratory tug springs back. The flick pair is the escape hatch for a short, fast throw
  // that never reaches 90px: 0.5px/ms is around twice a comfortable drag, and the 24px floor
  // is what stops a tap being read as one — a few pixels of finger roll over a 20ms contact
  // is an enormous apparent velocity and nothing else would reject it.
  var DRAG_DISMISS_PX = 90;
  var DRAG_FLICK_PX_PER_MS = 0.5;
  var DRAG_FLICK_MIN_PX = 24;
  var HEADER_FALLBACK_PX = 60;
  var THUMB_WIDTH = 150; // 44px slot at 3x DPR
  var A11Y_NAMESPACE = '.sticky-atc';
  var FOCUS_NAMESPACE = 'stickyAtc';
  // Footer clearance, on <html> to match the theme's own js-drawer-open convention.
  var CLEARANCE_CLASS = 'sticky-atc-clearance';
  var CLEARANCE_PROP = '--sticky-atc-height';
  var STRAY_SELECTOR = 'body > [data-sticky-atc], body > [data-sticky-sheet], body > [data-sticky-backdrop], body > [data-sticky-flash]';

  var instance = null;

  function escapeHtml(str) {
    var el = document.createElement('div');
    el.textContent = str;
    return el.innerHTML;
  }

  function money(cents) {
    return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
  }

  function resizedImage(src, width) {
    if (!src) return '';
    try {
      var url = new URL(src, location.href);
      url.searchParams.set('width', width);
      return url.href;
    } catch (e) {
      return src;
    }
  }

  // A .focus() that will actually land. `disabled` is the common case — the theme renders
  // the inline add-to-cart button disabled on a sold-out variant and theme.js's
  // updateCartButton keeps it in sync — but it is NOT the only one: offsetParent goes null
  // whenever an ancestor is display:none, which is how an app hiding the buy-buttons block
  // or a preorder template swap takes that button out. Both drop focus to <body> silently.
  // NOT a general-purpose predicate: offsetParent is also null for a position:fixed element,
  // so never point this at the bar or anything else fixed. Note visibility:hidden does NOT
  // null offsetParent, which is why the bar's own reachability is decided by the state flags
  // instead.
  function canFocus(el) {
    return !!el && !el.disabled && el.offsetParent !== null;
  }

  function headerOffset() {
    var declared = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    if (declared) return Math.round(declared);
    var header = document.querySelector('.site-header');
    return header && header.offsetHeight ? Math.round(header.offsetHeight) : HEADER_FALLBACK_PX;
  }

  function build(bar, form, root) {
    var configEl = bar.querySelector('[data-sticky-config]');
    var jsonEl = root.querySelector('[data-variant-json]');
    var inlineBtn = root.querySelector('button.add-to-cart[data-add-to-cart]');
    var inlineTextEl = inlineBtn && inlineBtn.querySelector('[data-add-to-cart-text]');
    var sheet = root.querySelector('[data-sticky-sheet]');
    var backdrop = root.querySelector('[data-sticky-backdrop]');
    var flash = root.querySelector('[data-sticky-flash]');
    var statusEl = bar.querySelector('[data-sticky-status]');
    if (!configEl || !jsonEl || !inlineTextEl || !sheet || !backdrop || !flash || !statusEl) return null;

    var config;
    var variants;
    try {
      config = JSON.parse(configEl.textContent);
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return null;
    }
    // Style-only products (hats) have no sizeIndex; size-only products have no styleIndex.
    // At least one axis must exist or there is nothing to pick.
    if ((!config.sizeIndex && !config.styleIndex) || !Array.isArray(variants) || !variants.length) return null;

    var addToCartText = inlineTextEl.getAttribute('data-default-text');
    if (!addToCartText) return null;

    var opener = bar.querySelector('[data-sticky-open]');
    var barSubmit = bar.querySelector('[data-sticky-bar-submit]');
    var barPrice = bar.querySelector('[data-sticky-bar-price]');
    var barHint = bar.querySelector('[data-sticky-bar-hint]');
    var barThumb = bar.querySelector('[data-sticky-thumb]');
    var closeBtn = sheet.querySelector('[data-sticky-close]');
    var styleRow = sheet.querySelector('[data-sticky-styles]');
    var sizeGrid = sheet.querySelector('[data-sticky-sizes]');
    var styleNameEl = sheet.querySelector('[data-sticky-style-name]');
    var sizeNameEl = sheet.querySelector('[data-sticky-size-name]');
    var sheetSubmit = sheet.querySelector('[data-sticky-sheet-submit]');
    var errorEl = sheet.querySelector('[data-sticky-error]');
    // The VISIBLE inline picker for this product: the size wrapper, or the style wrapper when
    // there is no size axis OR the size lookup can't be resolved. Either lookup can
    // legitimately return null — the variant-picker is an optional, reorderable section
    // block — in which case the inline add-to-cart button stays the only inline gate.
    // Resolved here with the other element lookups rather than at its observer below,
    // because closeSheet() reads it too. Reassigned once if Swatch King renders late — see
    // swatchMO. Swatch King is looked up on the preferred axis only: the app renders both
    // options, and accepting a style swatch that landed first would retarget to the wrong one.
    var pickerAxis = config.sizeIndex || config.styleIndex;
    var pickerWrap = swatchWrap(pickerAxis) ||
      (config.sizeIndex && optionWrap(config.sizeIndex)) ||
      (config.styleIndex && optionWrap(config.styleIndex)) || null;

    var sizeLabel = bar.getAttribute('data-size-label') || '';
    var hintDefaultText = barHint.getAttribute('data-default-text') || '';
    var sheetDefaultText = sheetSubmit.getAttribute('data-default-text') || '';
    var genericErrorText = errorEl.getAttribute('data-error-text') || '';
    // Guarded, not defaulted, and deliberately fatal like addToCartText above. Defaulting to
    // '' would announce nothing while the flash still APPEARS on screen — its visible span
    // is independent markup — so the add would silently stop being announced with no visual
    // symptom to catch it. Failing the whole bar is loud, and this string comes from a
    // hardcoded Liquid assign, so it can only go missing via a bad edit.
    var flashText = flash.getAttribute('data-flash-text');
    if (!flashText) return null;
    var initialThumbSrc = barThumb.getAttribute('src') || '';

    var styleButtons = styleRow ? [].slice.call(styleRow.querySelectorAll('[data-sticky-style]')) : [];
    var sizeButtons = sizeGrid ? [].slice.call(sizeGrid.querySelectorAll('[data-sticky-size]')) : [];
    var shortNames = {};
    styleButtons.forEach(function (btn) {
      shortNames[btn.getAttribute('data-sticky-style')] = btn.getAttribute('data-sticky-style-short') || '';
    });

    // Fixed positioning breaks under transformed ancestors — move to the body.
    document.querySelectorAll(STRAY_SELECTOR).forEach(function (el) { el.remove(); });
    [backdrop, sheet, bar, flash].forEach(function (el) { document.body.appendChild(el); });

    var state = {
      selectedStyle: null,
      selectedSize: null,
      variant: null,
      // The size the shopper deliberately chose, by VALUE — not a "they have chosen at some
      // point" latch. A latch only survived single-hop style changes: arrow keys walk the
      // shopper through every intermediate style, and one that doesn't stock the chosen size
      // would clear the latch with nothing to set it again, stranding them not-ready on a
      // style that does stock it. Comparing the value against the live selection needs no
      // clearing at all. Null on a style-only product, where it is never read.
      chosenSize: null
    };

    var sheetOpen = false;
    var dragging = false;
    // When the slide-in started, so a grab can be refused while it is still running — see
    // onSheetTouchStart. Needs no resetting anywhere: openSheet() is the only writer of both
    // this and sheetOpen, so they are always set together, and build() gives every instance
    // its own closure. A destroy()/re-init cycle therefore starts again at 0, which reads as
    // "long ago" and blocks nothing.
    var sheetOpenedAt = 0;
    var dragTouchId = null;
    var dragStartY = 0;
    var dragStartTime = 0;
    var dragOffset = 0;
    var drawerOpen = document.documentElement.classList.contains('js-drawer-open');
    var pastGallery = false;
    var inlineAtcVisible = false;
    var inlinePickerVisible = false;
    var pendingAdd = false;
    // The message the bar's error timer owns, so its delayed retraction can only take back
    // its own announcement and never something newer.
    var barErrorText = '';
    var hideTimer = null;
    var flashTimer = null;
    var loadingTimer = null;
    var pendingTimer = null;
    var errorTimer = null;
    var focusRescueTimer = null;

    // ====== DATA ======

    // Foreign pickers (quick-shop, featured-product) dispatch the same document-level
    // variant:change, and on size-only products an option-value match would collide
    // across products. Identity is the only safe test.
    function isOurVariant(variant) {
      return !!variant && variants.some(function (candidate) { return candidate.id === variant.id; });
    }

    // Only the optionN keys present in `expected` are compared, so a 2-option product
    // simply never tests option3.
    function variantMatches(variant, expected) {
      for (var index in expected) {
        if (!Object.prototype.hasOwnProperty.call(expected, index)) continue;
        if (variant[index] !== expected[index]) return false;
      }
      return true;
    }

    // The shopper's current selection, minus any axis the caller is about to overwrite or ignore.
    // Each caller's exclusion policy is explained in its own docblock — they differ, and the
    // differences are the interesting part. An absent config axis arrives here as `undefined`,
    // which no optionN key ever equals, so an unused skip argument excludes nothing.
    function expectedFrom(base, skipA, skipB) {
      var expected = {};
      for (var index in base) {
        if (Object.prototype.hasOwnProperty.call(base, index) && index !== skipA && index !== skipB) {
          expected[index] = base[index];
        }
      }
      return expected;
    }

    // Exact match on the style/size pair AND every other option the shopper has selected.
    // Matching only two axes on a three-option product (Options + Size + Colour) would price
    // and enable size tiles from the first variant of an unrelated colour.
    // `selected` is the map from selectedOptions(); pass one in to reuse it across a paint.
    function findVariant(styleValue, sizeValue, selected) {
      // Nothing skipped: style and size are overwritten just below rather than excluded.
      var base = selected || selectedOptions();
      var expected = expectedFrom(base);

      if (config.styleIndex) expected[config.styleIndex] = styleValue;
      // No size axis (style-only product): match on style + the other selected options only.
      if (config.sizeIndex) expected[config.sizeIndex] = sizeValue;

      for (var i = 0; i < variants.length; i++) {
        if (variantMatches(variants[i], expected)) return variants[i];
      }
      return null;
    }

    // A style is offerable when SOME available variant of it also matches the shopper's other
    // non-size selections. On a two-option product that degenerates to "any available variant
    // of that style"; on a three-option product it re-evaluates when the third option changes.
    function styleAvailable(styleValue, selected) {
      var base = selected || selectedOptions();
      var expected = expectedFrom(base, config.sizeIndex, config.styleIndex);

      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        if (variant[config.styleIndex] !== styleValue) continue;
        if (variant.available && variantMatches(variant, expected)) return true;
      }
      return false;
    }

    // Size tiles in the merchant's admin order.
    //
    // DO NOT "simplify" this back to Liquid / option.values order. Shopify's option.values is
    // FIRST-OCCURRENCE order across the whole style-major variant array, so a size carried by
    // only a later style lands at the END: on the Crucial Taunt tee the Size values come out
    // [S,M,L,XL,2XL,3XL,4XL,5XL,6XL,XS] because XS exists solely on Crop Baby Tee, the 4th
    // style. The real admin order lives inside each style's own variant subsequence
    // (Crop: XS,S,M,L,XL,2XL), so derive it from the variants matching everything currently
    // selected EXCEPT size — the same all-options philosophy as findVariant, which also covers
    // Colour+Size and third-option shapes and degenerates to "all variants" on a size-only
    // product. Returns the buttons themselves so the caller has nothing to look up.
    function orderedSizeButtons(selected) {
      var base = selected || selectedOptions();
      var expected = expectedFrom(base, config.sizeIndex);

      var order = [];
      for (var i = 0; i < variants.length; i++) {
        if (!variantMatches(variants[i], expected)) continue;
        var value = variants[i][config.sizeIndex];
        if (order.indexOf(value) === -1) order.push(value);
      }

      var ordered = [];
      order.forEach(function (value) {
        sizeButtons.forEach(function (btn) {
          if (btn.getAttribute('data-sticky-size') === value) ordered.push(btn);
        });
      });
      // Sizes the current selection has no variant for (disabled tiles) keep their existing
      // relative order at the end.
      sizeButtons.forEach(function (btn) {
        if (ordered.indexOf(btn) === -1) ordered.push(btn);
      });
      return ordered;
    }

    // Deliberately style-only: this is the "before a size is confirmed" display price, and it
    // has to agree with the style card price, which Liquid also derives from the style alone.
    function baseVariant() {
      if (config.styleIndex && state.selectedStyle) {
        for (var i = 0; i < variants.length; i++) {
          if (variants[i][config.styleIndex] === state.selectedStyle) return variants[i];
        }
      }
      return state.variant || variants[0];
    }

    // Also deliberately style-only: a thumbnail fallback, not a price or an availability
    // claim, so any image belonging to the style is an acceptable answer.
    function styleImageSrc(styleValue) {
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        if (config.styleIndex && variant[config.styleIndex] !== styleValue) continue;
        if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
      }
      return '';
    }

    // ====== FORM SYNC ======

    function optionWrap(index) {
      return root.querySelector('.variant-input-wrap[data-index="' + index + '"]');
    }

    // The Swatch King app's picker for one option. When the app is installed it hides the
    // theme's .variant-wrapper (display:none) and this is what the shopper actually sees, so
    // an observer on optionWrap() could never fire. Inserted by the app's JS — it is NOT in
    // the server HTML — and the node survives variant changes (verified live).
    function swatchWrap(index) {
      return root.querySelector('variant-swatch-king [option-target="' + index + '"]');
    }

    function wrapValue(wrap) {
      var inputs = wrap.querySelectorAll('[data-variant-input]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].tagName === 'SELECT') return inputs[i].value;
        if (inputs[i].checked) return inputs[i].value;
      }
      return null;
    }

    // The inline picker's own focusable control: the selected option, else the first one
    // that can take focus. Mirrors wrapValue()'s shape because the theme renders this
    // wrapper as either radios (variant-button.liquid) or a select (variant-dropdown.liquid).
    // The radios are 1px-clipped rather than display:none — theme.css.liquid styles
    // `input[type=radio]:focus + label` — so focusing one puts a real ring on its label, and
    // offsetParent resolves to the position:relative wrap rather than null. Swatch King's
    // tiles are li[role=radio][aria-checked] with their own roving tabindex.
    function pickerFocusTarget() {
      if (!pickerWrap) return null;
      var inputs = pickerWrap.querySelectorAll('[data-variant-input], [role="radio"]');
      var fallback = null;
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        if (!canFocus(input)) continue;
        if (input.tagName === 'SELECT' || input.checked || input.getAttribute('aria-checked') === 'true') return input;
        if (!fallback) fallback = input;
      }
      return fallback;
    }

    // Every option the shopper currently has selected, e.g. {option1: 'Tee - Black', option2: 'M'}.
    // Scoped to our section root; an option with no resolvable value is simply absent.
    function selectedOptions() {
      var selected = {};
      root.querySelectorAll('.variant-input-wrap[data-index]').forEach(function (wrap) {
        var index = wrap.getAttribute('data-index');
        var value = wrapValue(wrap);
        if (index && value !== null) selected[index] = value;
      });
      return selected;
    }

    // Read back from the theme's inputs (already updated by the time variant:change fires).
    // Scoped to our section, so a featured-product section elsewhere can't corrupt us.
    function reconcile(detailVariant) {
      var selected = selectedOptions();
      if (config.styleIndex) state.selectedStyle = selected[config.styleIndex] || null;
      state.selectedSize = config.sizeIndex ? (selected[config.sizeIndex] || null) : null;

      // theme.js rewrites the inputs before dispatching, so an accepted detail variant has
      // to agree with EVERY selected option, not just style and size.
      var matches = isOurVariant(detailVariant) && variantMatches(detailVariant, selected);

      state.variant = matches ? detailVariant : findVariant(state.selectedStyle, state.selectedSize, selected);
    }

    // Returns true when the form was actually mutated (a variant:change will follow).
    function setOption(index, value) {
      var wrap = optionWrap(index);
      if (!wrap) return false;
      var inputs = wrap.querySelectorAll('[data-variant-input]');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        if (input.tagName === 'SELECT') {
          if (input.value === value) return false;
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
        if (input.value !== value) continue;
        if (input.checked) return false;
        // .click() (not .checked = true) so variant-image-filter.js's capture-phase
        // hook updates image groups before theme.js rebuilds the gallery.
        input.click();
        return true;
      }
      return false;
    }

    // ====== PAINT ======

    // Not just "a size was tapped": the resolved variant must exist and be buyable.
    // theme.js:981 early-returns on an unresolvable combination, leaving the master
    // select stale — submitting then would add the wrong variant id.
    function isReady() {
      // Style-only product (hats): the default style is a visible, sensible default, so the
      // bar is ready straight away. The confirmation gate exists because sizes are personal.
      if (!config.sizeIndex) return !!state.variant && state.variant.available;
      // The theme pre-checks a size, so selectedSize is non-null from first paint while
      // chosenSize is still null — which is exactly the "not chosen yet" state we want.
      return state.chosenSize === state.selectedSize && !!state.variant && state.variant.available;
    }

    function hintText() {
      var short = shortNames[state.selectedStyle];
      var parts = [];
      if (config.sizeIndex && state.selectedSize) {
        parts.push((sizeLabel ? sizeLabel + ' ' : '') + state.selectedSize);
      }
      if (short) parts.push(short);
      // No chevron appended — it is its own element in the markup, so it survives both
      // this textContent write and the sub-line's ellipsis.
      return parts.join(' · ');
    }

    function paintThumb() {
      var src = (state.variant && state.variant.featured_image && state.variant.featured_image.src) ||
        styleImageSrc(state.selectedStyle) ||
        initialThumbSrc;
      var next = resizedImage(src, THUMB_WIDTH);
      if (next && barThumb.getAttribute('src') !== next) barThumb.setAttribute('src', next);
    }

    // A tile the shopper can actually act on. applyRovingTabindex() and groupItems() MUST
    // agree on this: the group's single tab stop has to be a tile the arrow keys can also
    // reach, or Tab lands on something the arrows then refuse to move away from. One
    // predicate so a new condition cannot be added to only one of them.
    function isOffered(btn) {
      return !btn.hidden && !btn.disabled;
    }

    // Roving tabindex: ONE tab stop per radiogroup. The selected tile is the group's entry
    // point, or the first offered tile when nothing is selected yet, so the group is always
    // reachable. Hidden/disabled tiles are never it — an entirely sold-out group ends up
    // with no tab stop rather than a dead one. Call once per group, per paint. Returns the
    // entry tile so a caller that has just lost focus can land on it.
    function applyRovingTabindex(buttons) {
      var offered = buttons.filter(isOffered);
      var entry = null;
      offered.forEach(function (btn) {
        if (!entry && btn.getAttribute('aria-checked') === 'true') entry = btn;
      });
      if (!entry) entry = offered[0] || null;
      buttons.forEach(function (btn) { btn.tabIndex = btn === entry ? 0 : -1; });
      return entry;
    }

    function paint() {
      var ready = isReady();
      var base = baseVariant();
      var priceCents = ready ? state.variant.price : (base ? base.price : null);

      barPrice.innerHTML = priceCents === null ? '' : money(priceCents);
      if (!bar.classList.contains('has-error')) {
        barHint.textContent = ready ? hintText() : hintDefaultText;
      }
      barSubmit.textContent = addToCartText;
      bar.classList.toggle('is-ready', ready);
      // The pulse is dismissed by the first deliberate size choice — which is exactly what a
      // non-null chosenSize records, so there is no second flag to keep in step. Style-only
      // products have nothing to prompt for, so they never pulse at all; that test HAS to stay
      // first, because chosenSize's docblock promises it is never read on those products.
      bar.classList.toggle('is-quiet', !config.sizeIndex || state.chosenSize !== null);
      paintThumb();

      // Read the option map once for the whole sheet rather than per card/tile.
      var selected = selectedOptions();

      if (styleNameEl) styleNameEl.textContent = state.selectedStyle || '';
      styleButtons.forEach(function (btn) {
        var value = btn.getAttribute('data-sticky-style');
        var on = value === state.selectedStyle;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        // Unbuyable styles are hidden outright, not disabled — the shopper never sees an
        // option they can't take. (The on-page picker keeps the theme's disabled treatment.)
        btn.disabled = !styleAvailable(value, selected);
        btn.hidden = btn.disabled;
      });
      applyRovingTabindex(styleButtons);

      // Style-only products render no size grid at all.
      if (sizeGrid) {
        if (sizeNameEl) sizeNameEl.textContent = ready ? (state.selectedSize || '') : '';
        // Sequence and state are painted in one pass so a tile's position can never disagree
        // with its state. Hidden tiles still get positioned; the grid just collapses them.
        var ordered = orderedSizeButtons(selected);
        var moved = false;

        ordered.forEach(function (btn, position) {
          var value = btn.getAttribute('data-sticky-size');
          var match = findVariant(state.selectedStyle, value, selected);
          var priceEl = btn.querySelector('[data-size-price]');
          var on = ready && value === state.selectedSize;
          if (priceEl) priceEl.innerHTML = match ? money(match.price) : '';
          // Sold out / nonexistent for this selection: hide rather than strike through.
          btn.disabled = !match || !match.available;
          btn.hidden = btn.disabled;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
          // The DOM is untouched in this pass, so children[] still reflects the old order.
          if (sizeGrid.children[position] !== btn) moved = true;
        });

        // appendChild moves in place. Only when the sequence actually changed — re-appending
        // a focused tile would blur it, and most repaints (size picks) reorder nothing.
        //
        // The focus rescue below is DEFENSIVE, not currently exercised: arrow keys in THIS
        // grid cannot cause a reorder, because orderedSizeButtons() derives the sequence
        // from every selected option EXCEPT size, so a size pick leaves it identical and
        // `moved` stays false. Only an on-page style change or a section reload can re-order
        // while focus sits in the grid. Keep it — a third option axis would make it live,
        // and it is two lines.
        var focused = null;
        if (moved) {
          focused = ordered.indexOf(document.activeElement) === -1 ? null : document.activeElement;
          ordered.forEach(function (btn) { sizeGrid.appendChild(btn); });
        }

        var entry = applyRovingTabindex(ordered);
        if (focused) {
          // If this pass hid the tile focus was on, appendChild has already dropped focus to
          // <body>. Landing on the group's new entry point beats dropping the shopper out of
          // the grid entirely. (.focus() on a hidden tile is a no-op, hence the isOffered
          // test rather than trusting the capture.)
          var target = isOffered(focused) ? focused : entry;
          if (target) target.focus();
        }
      }

      sheetSubmit.classList.toggle('is-ready', ready);
      sheetSubmit.setAttribute('aria-disabled', ready ? 'false' : 'true');
      if (ready) {
        sheetSubmit.innerHTML = escapeHtml(addToCartText) + ' · ' + money(state.variant.price);
      } else {
        sheetSubmit.textContent = sheetDefaultText;
      }
    }

    // ====== VISIBILITY ======

    // "Earned": the bar owns the bottom of the viewport — the gallery is behind us and no
    // inline control has taken the job over. Deliberately WITHOUT the modal suppressors,
    // because the footer clearance keys off this rather than off actual visibility.
    function barEarned() {
      return pastGallery && !inlineAtcVisible && !inlinePickerVisible;
    }

    // The full gate. Derived FROM barEarned() rather than restating it, so the scroll
    // conditions live in exactly one place and the two can't drift. Reads flags only and
    // mutates nothing, so it may be evaluated before or after applyVisibility() — that is
    // what lets closeSheet() ask where the bar will BE instead of reading back a class, and
    // it means a later edit moving the call can't resurrect the bug.
    function barShouldShow() {
      return barEarned() && !sheetOpen && !drawerOpen;
    }

    // Reserve the bar's height at the foot of the document, or the last footer row sits
    // underneath it at full scroll. MEASURED, not a literal: the bar's padding carries
    // env(safe-area-inset-bottom), which a hardcoded number gets wrong on a notched device.
    // offsetHeight reads fine while the bar is hidden — it hides with transform +
    // visibility, both of which keep it in layout.
    //
    // MEASURE BEFORE ANY CLASS TOGGLE. Do not move this read below one: neither `transform`
    // nor `visibility` changes the bar's border box, so the value is identical either way,
    // but write-read-write forces a synchronous style recalc + layout on every call, and
    // applyVisibility() can end up on a per-touchmove path.
    //
    // Keyed on barEarned(), NOT on actual visibility. Dropping the padding while a modal is
    // up shortens the document, and a shopper parked at max scroll is clamped down by the
    // bar's height — a clamp that closing does NOT undo, so one open/close at the bottom of
    // the page moves them permanently. It would be plainly visible too: the backdrop is a
    // 50% scrim over a sheet covering at most 82vh, and on iOS the page underneath is not
    // even scroll-locked (the @supports opt-out in the CSS). Keying on "earned" means the
    // strip is only ever reserved-but-unoccupied while a full-viewport modal is up, at the
    // very bottom of the viewport where the sheet already sits.
    function applyClearance(reserve) {
      // A zero measurement is never a real answer, and writing it is WORSE than writing
      // nothing. Above 768px the CSS sets `display: none !important` on the bar, so
      // offsetHeight reads 0 — and the gallery observer is not media-gated, so barEarned()
      // still goes true on desktop. Writing 0 would PIN the property there:
      // var(--sticky-atc-height, 69px) only falls back when the property is UNSET, so
      // crossing to mobile afterwards would leave the footer covered with nothing to
      // re-measure. Leaving it alone keeps whichever answer is right — the last good
      // measurement, or the CSS fallback.
      var height = reserve ? bar.offsetHeight : 0;
      if (height) document.documentElement.style.setProperty(CLEARANCE_PROP, height + 'px');
      document.documentElement.classList.toggle(CLEARANCE_CLASS, reserve);
    }

    function applyVisibility() {
      applyClearance(barEarned());
      bar.classList.toggle('is-visible', barShouldShow());
    }

    // ====== SHEET ======

    // theme.a11y.trapFocus binds focusin/focusout/keydown but removeTrapFocus only
    // unbinds focusin, and theme.delegate.on (theme.js:46-56) keys handlers by
    // namespaced event name — re-binding leaves the old listener attached. Unbind the
    // rest ourselves or every open/close cycle leaks handlers on <html>.
    function releaseFocusTrap() {
      theme.a11y.removeTrapFocus({ container: sheet, namespace: FOCUS_NAMESPACE });
      document.documentElement.off('focusout.' + FOCUS_NAMESPACE);
      document.documentElement.off('keydown.' + FOCUS_NAMESPACE);
    }

    function openSheet() {
      if (sheetOpen) return;
      sheetOpen = true;
      sheetOpenedAt = performance.now();
      clearTimeout(hideTimer);
      sheet.removeAttribute('hidden');
      backdrop.removeAttribute('hidden');
      void sheet.offsetHeight; // flush layout so the slide-in transition runs
      document.documentElement.classList.add('sticky-sheet-open');
      sheet.classList.add('is-open');
      sheet.setAttribute('aria-modal', 'true');
      backdrop.classList.add('is-open');
      opener.setAttribute('aria-expanded', 'true');
      applyVisibility();
      theme.a11y.lockMobileScrolling(A11Y_NAMESPACE);
      theme.a11y.trapFocus({ container: sheet, elementToFocus: closeBtn, namespace: FOCUS_NAMESPACE });
    }

    // Where focus goes when the sheet gives it up. ONE decision, shared by the synchronous
    // return in closeSheet() and the rescue in its hideTimer, so the two can never disagree.
    // Three candidates, in the order the shopper wants them:
    //
    //   The opener, when the bar will be there.
    //
    //   Otherwise the inline picker, WHEN IT IS ON SCREEN. Not a nicety: the picker being
    //   visible is exactly what suppresses the bar, and that happens while the inline button
    //   is still below the fold. .focus() scrolls an off-screen element into view, so
    //   reaching past a visible picker for the button yanks the page downward on every
    //   Escape, backdrop tap and swipe dismiss.
    //
    //   Otherwise the inline add-to-cart button, if it can take focus at all.
    //
    // null means "leave focus alone" — better than .focus() no-oping onto <body>.
    // barShouldShow() reads flags and mutates nothing, so this is safe to call before or
    // after applyVisibility(); callers need not order themselves around it.
    function focusReturnTarget() {
      if (barShouldShow()) return opener;
      if (inlinePickerVisible) {
        var picker = pickerFocusTarget();
        if (picker) return picker;
      }
      return canFocus(inlineBtn) ? inlineBtn : null;
    }

    // skipFocusReturn: set on the add path, where the app's (or theme's) cart drawer takes
    // over the UI and returning focus to the bar would fight it. The scroll lock is always
    // released here — the sheet closes BEFORE any add starts, so no drawer holds it yet.
    function closeSheet(skipFocusReturn) {
      if (!sheetOpen) return;
      sheetOpen = false;
      // Ends any swipe in progress. Two jobs, one line:
      //
      // 1. Escape, the backdrop and the close button can all fire mid-drag. Without this they
      //    would leave an inline transform pinning the sheet where the finger was and
      //    `.is-dragging` holding its transition off, so it would sit there until `hidden`
      //    landed SHEET_ANIM_MS later and it vanished without animating.
      // 2. It is the swipe's OWN dismiss path. resetDrag() drops the class and the transform
      //    in the SAME tick as the `.is-open` removal below, which is a single style recalc:
      //    the before-change style is still the finger's last position and the after-change
      //    style is `translateY(110%)` with the transition restored, so the slide-out starts
      //    from where the finger let go instead of snapping back to open first.
      //
      // The invariant is that both halves land in the same tick as the `.is-open` removal, so
      // the browser only ever sees the finished state. The two halves fail differently if
      // split: deferring the `.is-open` removal lets the sheet transition back to the OPEN
      // position first and slide out from there, which is the bug worth guarding; deferring
      // resetDrag() is milder, since `.is-dragging` still holds `transition: none` and the
      // inline transform still beats `translateY(110%)`, so it merely stalls for a frame and
      // then slides out correctly from the release point. (Position within the tick is not
      // load-bearing — applyVisibility() below forces a recalc via bar.offsetHeight and the
      // transition starts correctly on either side of it — but there is no reason to depend
      // on that.)
      resetDrag();
      sheet.classList.remove('is-open');
      // Cleared HERE, not on hideTimer. The class only starts the slide-out; the sheet stays
      // rendered until `hidden` lands SHEET_ANIM_MS later. Verified over CDP that during
      // that window `hidden` is not yet applied, display is still flex, and the dialog is
      // still exposed with modal=true. Blink does not itself prune content outside a modal
      // — the screen reader does, off exactly that property — so leaving it set is the
      // condition under which AT suppress the bar's status region. A warm cart/add.js round
      // trip finishes well inside 340ms, so an add lands squarely in that window.
      // Do not fold this into the hideTimer callback.
      sheet.setAttribute('aria-modal', 'false');
      backdrop.classList.remove('is-open');
      document.documentElement.classList.remove('sticky-sheet-open');
      opener.setAttribute('aria-expanded', 'false');
      releaseFocusTrap();
      clearError();

      theme.a11y.unlockMobileScrolling(A11Y_NAMESPACE);

      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        sheet.setAttribute('hidden', '');
        backdrop.setAttribute('hidden', '');
      }, SHEET_ANIM_MS);

      applyVisibility();

      // sheetOpen is already false above, so focusReturnTarget() sees the complete gate.
      // The add path skips this deliberately and is caught by the rescue in the hideTimer.
      if (!skipFocusReturn) {
        var focusTarget = focusReturnTarget();
        if (focusTarget) focusTarget.focus();
      }
    }

    // ====== SWIPE TO DISMISS ======

    // Every trace of a drag, removed together. Idempotent, and it calls nothing back —
    // closeSheet() and destroy() both invoke it, so it must not invoke either of them.
    // Clearing the inline transform is what hands the sheet back to the stylesheet:
    // `translateY(110%)` if `.is-open` has gone with it, or `none` for a spring-back.
    function resetDrag() {
      if (!dragging) return;
      dragging = false;
      dragTouchId = null;
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
    }

    // Is the finger that STARTED the drag among the ones this event is reporting?
    //
    // Identity, not `evt.touches.length`. A stray finger — a knuckle, a second hand steadying
    // the phone — can land inside the sheet and lift again without ever producing a touchmove,
    // so the multi-touch abort in onSheetTouchMove never runs. Its touchend then bubbles to
    // the same sheet listener while OUR finger is still down and mid-drag, and a handler that
    // does not check identity finalises the whole gesture on our offset and start time:
    // dismissing or springing back under a finger that is still dragging.
    //
    // A `touches.length` test cannot fix that — it is wrong in both directions. When the stray
    // lifts first, the remaining count is 1 and the test would wave the gesture through; when
    // ours lifts first, the count is still 1 (the stray) and the test would swallow the real
    // release. Matching identifiers is correct for every ordering.
    function isDragTouch(evt) {
      for (var i = 0; i < evt.changedTouches.length; i++) {
        if (evt.changedTouches[i].identifier === dragTouchId) return true;
      }
      return false;
    }

    // The grab zone is the handle and the header row, tagged [data-sticky-grab] in the
    // markup — never .sticky-sheet__body, which owns the style-card and size-grid scrolling.
    // A touch outside it never sets `dragging`, so the move handler below returns at once.
    //
    // The close button sits INSIDE that zone and is deliberately not excluded, so every tap on
    // it also opens and closes a zero-distance drag. That is safe only because both gestures
    // want the same thing: a tap leaves dragOffset at 0, springs back invisibly and lets the
    // click through to closeSheet(), while a real drag off the button dismisses — the same
    // outcome by the other route. Exclude the button here and you gain nothing; add an
    // interactive control to this zone whose action ISN'T "close" and that stops being true.
    //
    // `sheetOpen` is checked because the sheet stays rendered and touchable for the whole
    // SHEET_ANIM_MS slide-out; there is nothing to drag once it is on its way out.
    //
    // The mirror case needs the latch. openSheet() sets sheetOpen and `.is-open` SYNCHRONOUSLY,
    // so the sheetOpen test passes for the entire slide-IN too — and adding `.is-dragging`
    // mid-transition applies `transition: none`, which cancels the running transition and jumps
    // the computed transform from wherever it had reached straight to `.is-open`'s
    // `transform: none`. The sheet visibly LEAPS fully open and only then starts following the
    // finger. Anyone who taps the bar and reaches straight for the handle hits it, which is
    // most likely right after a mis-tap. Refusing the grab for SHEET_ANIM_MS costs a shopper
    // nothing — the sheet they want to dismiss is still arriving.
    //
    // This is the ONLY place `.is-dragging` meets a transition it did not start, so the fix
    // belongs here and not in the CSS: deleting `transition: none` would stop the leap by
    // destroying the 1:1 finger tracking that class exists to provide.
    //
    // `evt.touches` is document-wide, not sheet-local: a thumb already resting on the backdrop
    // blocks the drag. Benign (lifting that thumb clicks the backdrop, which closes the sheet
    // anyway) and stricter than dragTouchId now strictly requires — left alone deliberately.
    function onSheetTouchStart(evt) {
      if (dragging || !sheetOpen || evt.touches.length > 1) return;
      if (performance.now() - sheetOpenedAt < SHEET_ANIM_MS) return;
      if (!evt.target.closest('[data-sticky-grab]')) return;
      dragging = true;
      // changedTouches[0], not touches[0]: identical here (the guard above means there is
      // exactly one), but it names the finger that actually started this gesture, which is
      // the one isDragTouch() has to recognise on the way out.
      dragTouchId = evt.changedTouches[0].identifier;
      dragStartY = evt.changedTouches[0].clientY;
      dragStartTime = evt.timeStamp;
      dragOffset = 0;
      sheet.classList.add('is-dragging');
    }

    // No preventDefault(), hence the explicit `passive: true` at the wire-up: the grab zone
    // carries `touch-action: none` in the CSS, so the browser has already handed the gesture
    // over and there is no default scroll left to cancel. Declaring it passive also makes a
    // later preventDefault() fail loudly in the console rather than silently do nothing.
    // No stopPropagation() either — the only touchmove listener above us is the inert one
    // theme.a11y.lockMobileScrolling binds on <html> (theme.js: its whole body is
    // `return true`), and stopping the event would cost the sheet nothing and could break a
    // future listener that has as much right to see it as we do.
    function onSheetTouchMove(evt) {
      if (!dragging) return;
      // A second finger escalates the gesture into something we are not tracking — abandon
      // it and let the sheet spring back rather than follow touches[0] through a pinch.
      if (evt.touches.length > 1) {
        resetDrag();
        return;
      }
      // Downward only: an upward drag must not lift the sheet above its open position.
      dragOffset = Math.max(0, evt.touches[0].clientY - dragStartY);
      sheet.style.transform = 'translateY(' + dragOffset + 'px)';
    }

    // Distance OR flick. Velocity is measured across the whole gesture rather than the last
    // sample pair, which is both simpler and better behaved at the edges: a finger that
    // travels far and then rests reads as the slow drag it was and springs back, and a
    // single jittery final sample cannot manufacture a dismiss on its own.
    //
    // The trade was considered and taken deliberately: a drag that dawdles to ~60px and then
    // ends in a genuine fast flick to ~85px averages out slow and springs back, so that
    // dismiss is missed. Whole-gesture measurement can only ever MISS a dismiss, never invent
    // one, and the shopper's recovery is to swipe again; last-sample velocity trades that for
    // dismissing the sheet on a twitch. Do not "fix" this to a trailing-sample window.
    function onSheetTouchEnd(evt) {
      if (!dragging || !isDragTouch(evt)) return;
      var elapsed = evt.timeStamp - dragStartTime;
      var flicked = dragOffset >= DRAG_FLICK_MIN_PX && elapsed > 0 &&
        (dragOffset / elapsed) >= DRAG_FLICK_PX_PER_MS;

      // closeSheet() is the single dismissal path — it owns the focus return, the scroll
      // lock, aria-modal and the `hidden` timer — and it calls resetDrag() itself, in the
      // same tick that it drops `.is-open`. Nothing to unwind here.
      if (dragOffset >= DRAG_DISMISS_PX || flicked) closeSheet();
      else resetDrag();
    }

    // Same identity test as the release: a system gesture that cancels only a stray finger
    // must not abandon a drag our finger is still performing.
    function onSheetTouchCancel(evt) {
      if (!dragging || !isDragTouch(evt)) return;
      resetDrag();
    }

    // ====== ADD TO CART ======

    function setLoading(on) {
      [barSubmit, sheetSubmit].forEach(function (btn) {
        btn.classList.toggle('is-loading', on);
        btn.disabled = on;
      });
    }

    // The bar's single announcement channel — errors and the "added" confirmation both land
    // in [data-sticky-status]. It sits empty between messages, so every write is an
    // empty-to-text mutation rather than a same-value rewrite an engine is free to ignore.
    function announce(message) {
      statusEl.textContent = message;
    }

    // Each writer takes back only its OWN message. Both retractions are on a delay (the
    // error timer, the flash timer), and whichever fires second must not wipe a message that
    // arrived in between. onAdded and onAddError are mutually exclusive per add, so today
    // this only bites across two adds inside BAR_ERROR_MS — it is a guard, not a live bug.
    function retract(message) {
      if (statusEl.textContent === message) statusEl.textContent = '';
    }

    function clearError() {
      clearTimeout(errorTimer);
      errorEl.textContent = '';
      if (bar.classList.contains('has-error')) {
        retract(barErrorText);
        barErrorText = '';
        bar.classList.remove('has-error');
        paint();
        // The hint is back to one nowrap line, so the bar has shrunk again. See showError().
        applyClearance(barEarned());
      }
    }

    function showError(message) {
      clearTimeout(errorTimer);
      if (sheetOpen) {
        // The sheet's own paragraph is aria-live; the bar's region is behind the sheet's
        // aria-modal and would be pruned anyway.
        errorEl.textContent = message;
        return;
      }
      bar.classList.add('has-error');
      barHint.textContent = message;
      barErrorText = message;
      announce(message);
      // .has-error swaps the hint from one nowrap line to a two-line clamp, so with
      // --typeBaseLineHeight toward the top of its 1-2 range the text column can overtake
      // the 46px thumb and the bar gets taller. Nothing else calls applyVisibility() here,
      // so without this the reserved strip stays short for the whole BAR_ERROR_MS.
      applyClearance(barEarned());
      errorTimer = setTimeout(clearError, BAR_ERROR_MS);
    }

    // theme.js forwards Shopify's `description`, which is a string for the errors worth
    // showing and an object otherwise — nothing shopper-readable in that branch.
    function errorText(errorMessage) {
      return typeof errorMessage === 'string' && errorMessage ? errorMessage : genericErrorText;
    }

    // MUST be a real click on the inline button. Do NOT "modernize" this to
    // form.requestSubmit() — that is what caused every sticky add to add TWO units.
    //
    // The Monster Upsells app extension (cart_renderer_v2.js) installs two hijack layers:
    // a delegated CLICK hijack on the add button that stopImmediatePropagation()s and runs
    // its own add, and a document-level SUBMIT hijack on form[action*='/cart/add'].
    // requestSubmit() fires no click, so it slipped past the click layer, reached
    // theme.AjaxProduct's form-level listener (add #1), then bubbled to the app's
    // document-level submit hijack (add #2). Clicking the real button takes the exact path
    // the inline button takes: the app handles it when installed, and a native submit
    // reaches theme.AjaxProduct if the app is ever removed.
    // Put focus somewhere usable after an add, when nothing else has claimed it.
    //
    // Normally the cart drawer claims it: CartDrawer.open() traps focus into #CartDrawerForm
    // inside the ajaxProduct:added listener (theme.js ~4551). But a 422 opens no drawer, and
    // the Monster Upsells app dispatches no ajaxProduct:* events at all — with that app
    // installed NOTHING ever claims it, so this runs on every sticky add.
    //
    // Fires only when nobody else holds focus: still inside the sheet, or already on <body>.
    // Both are reachable — setLoading(true) disables the very button that was tapped, and
    // disabling the focused element blurs it to <body>, so focus has usually gone before
    // this runs. (Verified over CDP: activeElement is BODY at click time on every add path.
    // A guard of only sheet.contains() therefore never fires and the fix is inert.) If a
    // drawer holds focus, activeElement is neither and we leave it alone.
    //
    // Racing the drawer the other way is harmless: theme.a11y.trapFocus calls
    // elementToFocus.focus() unconditionally (theme.js:198), so a drawer opening after this
    // simply takes focus back.
    function rescueFocus() {
      var active = document.activeElement;
      if (active && active !== document.body && !sheet.contains(active)) return;
      var target = focusReturnTarget();
      if (target) target.focus();
    }

    function submitAdd() {
      // pendingAdd, not button state, is the real re-entry guard: when the app owns the add
      // its click hijack never reaches theme.AjaxProduct's own status.loading guard.
      if (!isReady() || pendingAdd) return;
      pendingAdd = true;
      clearError();
      setLoading(true);
      clearTimeout(loadingTimer);
      clearTimeout(pendingTimer);
      // An app-owned add emits no theme events, so both of these are fallbacks on two clocks:
      // the button re-enables quickly so the UI doesn't feel stuck, while pendingAdd keeps
      // blocking a second add for the full window. Neither claims success or failure.
      loadingTimer = setTimeout(function () { setLoading(false); }, LOADING_CLEAR_MS);
      pendingTimer = setTimeout(function () { pendingAdd = false; }, ADD_TIMEOUT_MS);
      // The focus rescue belongs HERE, not in closeSheet()'s hideTimer. submitAdd() is the
      // only thing that defers focus (the sole caller passing skipFocusReturn) and the only
      // thing that blurs it, so it owns putting it back. Hanging it off closeSheet missed
      // the PRIMARY path completely: onBarSubmit() adds directly once ready, the sheet was
      // never opened, closeSheet(true) early-returns on !sheetOpen, and no hideTimer is
      // ever scheduled — exactly the common case the ready state exists to enable.
      //
      // SHEET_ANIM_MS is reused rather than given its own constant: it is long enough for a
      // drawer to claim focus first, and when the sheet WAS open it is also when `hidden`
      // lands. Ordering against that is immaterial — focusReturnTarget() never returns an
      // element inside the sheet.
      clearTimeout(focusRescueTimer);
      focusRescueTimer = setTimeout(rescueFocus, SHEET_ANIM_MS);

      // The cart drawer that opens next is the confirmation, so get out of its way first.
      // Closing before the click also means no drawer holds the scroll lock yet, and it is
      // what gets the bar's status region out of the sheet's aria-modal pruning shadow — a
      // live region firing while an aria-modal dialog is open is invisible to most AT.
      // closeSheet() clears aria-modal ITSELF and immediately; it does not rely on the class
      // toggle, and it does not wait for `hidden` to land SHEET_ANIM_MS later, which a warm
      // cart/add.js round trip would beat. Do not reorder this below the click.
      closeSheet(true);
      inlineBtn.click();
    }

    // The flash element is aria-hidden decoration; the status region does the announcing.
    // Do NOT move the announcement back onto the flash: its text is in the DOM from page
    // load, so the only way to announce from there was blanking and restoring it on the next
    // frame — which worked purely because Blink and WebKit fire live-region notifications off
    // an internal dirty flag rather than a value diff.
    function showFlash() {
      flash.classList.add('is-visible');
      announce(flashText);
      clearTimeout(flashTimer);
      flashTimer = setTimeout(function () {
        flash.classList.remove('is-visible');
        retract(flashText);
      }, FLASH_MS);
    }

    // ====== HANDLERS ======

    // Nothing to clear here. A style that doesn't stock the chosen size simply resolves no
    // variant, so isReady() already reports not-ready — and moving on to a style that DOES
    // stock it becomes ready again on its own. (This used to unset a sizeChosen latch, which
    // arrow-key navigation turned destructive: it walks the shopper through every
    // intermediate style, and nothing re-set the latch on the far side.)
    function onStyleClick(evt) {
      var btn = evt.target.closest('[data-sticky-style]');
      if (!btn || btn.disabled) return;
      if (!setOption(config.styleIndex, btn.getAttribute('data-sticky-style'))) {
        reconcile(null);
        paint();
      }
    }

    function onSizeClick(evt) {
      var btn = evt.target.closest('[data-sticky-size]');
      if (!btn || btn.disabled) return;
      // Tapping the theme's pre-checked size produces no variant:change, so this has to be
      // recorded here too — it is the shopper's first deliberate choice.
      state.chosenSize = btn.getAttribute('data-sticky-size');
      if (!setOption(config.sizeIndex, state.chosenSize)) {
        reconcile(null);
        paint();
      }
    }

    // Members are read from the LIVE DOM on every keypress, never captured once: paint()
    // re-orders the size grid into admin order and hides sold-out tiles, so a cached list
    // goes stale the moment a style changes. Selected on [role="radio"] rather than the
    // usual data- hook so ONE helper serves both groups — the role IS the contract here.
    function groupItems(group) {
      return [].slice.call(group.querySelectorAll('[role="radio"]')).filter(isOffered);
    }

    // Standard WAI-ARIA radio-group navigation, selection follows focus, arrows wrap.
    function onGroupKeydown(evt) {
      var tile = evt.target.closest('[role="radio"]');
      if (!tile) return;
      var items = groupItems(evt.currentTarget);
      // Nothing to move between: a single style, or one size left in stock.
      if (items.length < 2) return;

      var current = items.indexOf(tile);
      if (current === -1) return;

      var next;
      if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') next = (current + 1) % items.length;
      else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') next = (current + items.length - 1) % items.length;
      else if (evt.key === 'Home') next = 0;
      else if (evt.key === 'End') next = items.length - 1;
      else return; // not ours — Tab, Escape and everything else pass straight through

      evt.preventDefault();
      items[next].focus();
      // Selection follows focus, routed through .click() so it takes the SAME path a tap
      // takes: onStyleClick / onSizeClick drive the theme's real inputs, and there stays
      // exactly one selection pipeline. Space and Enter need nothing — these are real
      // <button>s. Focus first, then click: the click can repaint and re-order the grid,
      // and paint()'s reorder branch restores focus to whatever held it.
      items[next].click();
    }

    function onVariantChange(evt) {
      var detail = evt.detail || {};
      // An on-page size pick counts as deliberate too. A style change that rewrites the
      // size (dynamic variants) reports the STYLE index, so it correctly doesn't count.
      if (config.sizeIndex && detail.index === config.sizeIndex && isOurVariant(detail.variant)) {
        state.chosenSize = detail.variant[config.sizeIndex];
      }
      reconcile(detail.variant);
      paint();
    }

    function onBarSubmit() {
      if (isReady()) submitAdd();
      else openSheet();
    }

    function onSheetSubmit() {
      submitAdd();
    }

    function onKeydown(evt) {
      if (evt.key === 'Escape' && sheetOpen) closeSheet();
    }

    function onAdded(evt) {
      if (evt.target !== form || !evt.detail || !evt.detail.product || !pendingAdd) return;
      pendingAdd = false;
      clearTimeout(loadingTimer);
      clearTimeout(pendingTimer);
      setLoading(false);
      // The sheet is already closed by submitAdd. Only fires in the app-less/theme path —
      // when the upsell app owns the add, its own drawer is the confirmation.
      showFlash();
    }

    function onAddError(evt) {
      if (evt.target !== form || !pendingAdd) return;
      pendingAdd = false;
      clearTimeout(loadingTimer);
      clearTimeout(pendingTimer);
      setLoading(false);
      showError(errorText(evt.detail && evt.detail.errorMessage));
    }

    // ====== WIRE UP ======

    opener.addEventListener('click', openSheet);
    barSubmit.addEventListener('click', onBarSubmit);
    closeBtn.addEventListener('click', function () { closeSheet(); });
    backdrop.addEventListener('click', function () { closeSheet(); });
    sheetSubmit.addEventListener('click', onSheetSubmit);
    // All four sit on the sheet itself, which destroy() removes — no explicit unbinding.
    // touchmove/touchend/touchcancel are dispatched at the element the touch STARTED on for
    // the whole sequence, so a finger that wanders off the grab zone (or off the sheet) still
    // bubbles through here. `passive: true` is asserted, not assumed, on the two that could
    // plausibly gain a preventDefault() later: see onSheetTouchMove. The other two carry no
    // options because touchend and touchcancel have no default action to cancel — the flag
    // would be inert, and an inert flag reads as though it were doing something.
    sheet.addEventListener('touchstart', onSheetTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onSheetTouchMove, { passive: true });
    sheet.addEventListener('touchend', onSheetTouchEnd);
    // A system gesture, an incoming call, or the multi-touch bail in onSheetTouchMove.
    sheet.addEventListener('touchcancel', onSheetTouchCancel);
    // Both radiogroup containers live inside the sheet, which destroy() removes — so these
    // need no explicit unbinding.
    if (sizeGrid) {
      sizeGrid.addEventListener('click', onSizeClick);
      sizeGrid.addEventListener('keydown', onGroupKeydown);
    }
    if (styleRow) {
      styleRow.addEventListener('click', onStyleClick);
      styleRow.addEventListener('keydown', onGroupKeydown);
    }

    document.addEventListener('variant:change', onVariantChange);
    document.addEventListener('keydown', onKeydown);
    form.addEventListener('ajaxProduct:added', onAdded);
    form.addEventListener('ajaxProduct:error', onAddError);

    var gallery = root.querySelector('[data-product-images]') || root.querySelector('.product__photos');
    var galleryIO = null;
    if (gallery) {
      galleryIO = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        // Passed ABOVE the header edge — not merely out of view below the fold.
        var passed = !entry.isIntersecting &&
          entry.boundingClientRect.bottom <= (entry.rootBounds ? entry.rootBounds.top : 0);
        if (!passed) return;

        // One-way latch: once the bar has been earned it stays earned. The theme's
        // scroll-to-photo scrolls the gallery back into view after a variant pick, which
        // would otherwise yank the bar away mid-interaction. inlineAtcVisible / sheetOpen /
        // drawerOpen still suppress it. Nothing sets this back to false, so the observer
        // has no more work to do.
        pastGallery = true;
        galleryIO.disconnect();
        applyVisibility();
      }, { rootMargin: '-' + headerOffset() + 'px 0px 0px 0px' });
      galleryIO.observe(gallery);
    }

    var atcIO = new IntersectionObserver(function (entries) {
      inlineAtcVisible = entries[entries.length - 1].isIntersecting;
      applyVisibility();
    });
    atcIO.observe(inlineBtn);

    // TWO-WAY, deliberately unlike the gallery latch above: while the inline picker is
    // on screen the bar's opener is a second, competing picker on the same screen, so this
    // one has to un-suppress as well as suppress.
    //
    // No rootMargin, unlike galleryIO twenty lines up — that asymmetry is deliberate. The
    // binding edge here is the BOTTOM one, the picker rising into view from below, and the
    // header inset only shifts the top edge; the top edge is already covered by atcIO,
    // since the inline button sits below the picker. A picker taller than the viewport
    // keeps the bar suppressed for its whole traverse, which is the intended semantics.
    //
    // Filtered by target because a retarget (below) can still deliver a queued entry for the
    // element it just stopped observing.
    var pickerIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === pickerWrap) inlinePickerVisible = entry.isIntersecting;
      });
      applyVisibility();
    });
    if (pickerWrap) pickerIO.observe(pickerWrap);

    // Swatch King renders after DOMContentLoaded, so on first build pickerWrap is usually the
    // theme's hidden wrapper. Watch the section for the app's picker and retarget ONCE when it
    // lands, then stop. If the app is absent this stays connected (childList only, one
    // querySelector per batch) until destroy().
    var swatchMO = null;
    if (!swatchWrap(pickerAxis)) {
      swatchMO = new MutationObserver(function () {
        var swatch = swatchWrap(pickerAxis);
        if (!swatch) return;
        swatchMO.disconnect();
        if (pickerWrap) pickerIO.unobserve(pickerWrap);
        pickerWrap = swatch;
        pickerIO.observe(pickerWrap);
      });
      swatchMO.observe(root, { childList: true, subtree: true });
    }

    // The bar's own height is the clearance's only input, and it changes for reasons we have
    // no other hook for: crossing 768px (display:none, measures 0), and
    // env(safe-area-inset-bottom) differing between portrait and landscape — which is what
    // retires the orientation-change limitation this feature shipped with. showError() and
    // clearError() still call applyClearance() directly; those are synchronous so the strip
    // is never stale for even a frame, and this is the catch-all for everything else.
    // No feedback loop: the clearance changes body's padding, which cannot resize a
    // position:fixed, full-width bar.
    var barRO = new ResizeObserver(function () { applyClearance(barEarned()); });
    barRO.observe(bar);

    // The cart drawer emits no close event — its class on <html> is the only signal.
    var drawerMO = new MutationObserver(function () {
      var open = document.documentElement.classList.contains('js-drawer-open');
      if (open === drawerOpen) return;
      drawerOpen = open;
      applyVisibility();
    });
    drawerMO.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    reconcile(null);
    paint();

    function destroy() {
      if (galleryIO) galleryIO.disconnect();
      atcIO.disconnect();
      pickerIO.disconnect();
      if (swatchMO) swatchMO.disconnect();
      barRO.disconnect();
      drawerMO.disconnect();

      document.removeEventListener('variant:change', onVariantChange);
      document.removeEventListener('keydown', onKeydown);
      form.removeEventListener('ajaxProduct:added', onAdded);
      form.removeEventListener('ajaxProduct:error', onAddError);

      clearTimeout(hideTimer);
      clearTimeout(flashTimer);
      clearTimeout(loadingTimer);
      clearTimeout(pendingTimer);
      clearTimeout(errorTimer);
      clearTimeout(focusRescueTimer);

      // The one piece of state the remove-the-element contract does NOT cover. A touch
      // sequence keeps being dispatched at its original target after that target leaves the
      // DOM, so a drag in flight when the section reloads would run touchend against a dead
      // instance and closeSheet() would re-add the classes destroy() is about to remove.
      // Clearing `dragging` makes every one of those handlers return at its first line.
      resetDrag();
      releaseFocusTrap();
      if (sheetOpen) theme.a11y.unlockMobileScrolling(A11Y_NAMESPACE);
      document.documentElement.off('touchmove' + A11Y_NAMESPACE);
      document.documentElement.classList.remove('sticky-sheet-open');
      // Both halves of the footer clearance: the class alone would leave a stale custom
      // property behind, and the property alone would leave real padding on the body.
      document.documentElement.classList.remove(CLEARANCE_CLASS);
      document.documentElement.style.removeProperty(CLEARANCE_PROP);

      [bar, sheet, backdrop, flash].forEach(function (el) { el.remove(); });
    }

    return { sectionId: root.getAttribute('data-section-id'), destroy: destroy };
  }

  // expectedSectionId: set by section:load so an unrelated section reload can't
  // spin us up against a product section that never changed.
  function init(expectedSectionId) {
    if (instance) return;
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;
    // Fail closed: without the real product form there is nothing to submit.
    var form = document.getElementById(bar.getAttribute('data-form-id'));
    var root = document.getElementById(bar.getAttribute('data-section-root'));
    if (!form || !root) return;
    if (expectedSectionId && root.getAttribute('data-section-id') !== expectedSectionId) return;
    instance = build(bar, form, root);
  }

  function destroy() {
    if (!instance) return;
    instance.destroy();
    instance = null;
  }

  // Wrapped: init() takes an argument, and the DOMContentLoaded event object isn't it.
  document.addEventListener('DOMContentLoaded', function () { init(); });

  document.addEventListener('shopify:section:load', function (evt) {
    var sectionId = evt.detail && evt.detail.sectionId;
    if (!sectionId) return;
    if (instance && instance.sectionId !== sectionId) return;
    destroy();
    init(sectionId);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var sectionId = evt.detail && evt.detail.sectionId;
    if (!instance || instance.sectionId !== sectionId) return;
    destroy();
  });
})();
